import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articlesAPI } from '../utils/api';
import { useRegion } from '../context/RegionContext';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { getArticleImage, handleImageError } from '../utils/imageUtils';

/**
 * RelatedArticles Component
 * Displays related articles based on category, tags, and brand mentions
 * 
 * @param {Object} currentArticle - The current article being viewed
 * @param {number} limit - Maximum number of related articles to show
 */
export const RelatedArticles = ({ currentArticle, limit = 6 }) => {
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [relatedDeals, setRelatedDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentRegion, region } = useRegion();
  
  useEffect(() => {
    const fetchRelatedContent = async () => {
      if (!currentArticle) return;
      
      try {
        // Fetch articles from same category
        const categoryArticles = await articlesAPI.getAll({
          category_id: currentArticle.category_id,
          is_published: true,
          region: currentRegion !== 'global' ? currentRegion : undefined,
          limit: limit + 5
        });
        
        // Filter out current article and prioritize by tags
        const filtered = categoryArticles.data
          .filter(a => a.id !== currentArticle.id)
          .map(article => {
            // Calculate relevance score based on shared tags
            const sharedTags = article.tags?.filter(
              tag => currentArticle.tags?.includes(tag)
            ).length || 0;
            return { ...article, relevanceScore: sharedTags };
          })
          .sort((a, b) => b.relevanceScore - a.relevanceScore)
          .slice(0, limit);
        
        // Separate into articles and deals (articles with deal_boxes)
        const deals = filtered.filter(a => a.deal_boxes && a.deal_boxes.length > 0);
        const articles = filtered.filter(a => !a.deal_boxes || a.deal_boxes.length === 0);
        
        setRelatedArticles(articles.slice(0, Math.ceil(limit / 2)));
        setRelatedDeals(deals.slice(0, Math.floor(limit / 2)));
      } catch (error) {
        console.error('Failed to fetch related content:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRelatedContent();
  }, [currentArticle, currentRegion, limit]);
  
  const buildArticleUrl = (slug) => {
    return currentRegion === 'global' ? `/article/${slug}` : `/${currentRegion}/article/${slug}`;
  };
  
  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-200 h-64 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }
  
  if (relatedArticles.length === 0 && relatedDeals.length === 0) {
    return null;
  }
  
  return (
    <section className="border-t-2 border-gray-900 pt-12 mt-12" data-testid="related-articles-section">
      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-playfair font-bold text-brand-green">
              Related Articles
            </h2>
            <Link 
              to={currentRegion === 'global' ? '/search' : `/${currentRegion}/search`}
              className="text-brand-red hover:underline flex items-center gap-1 font-medium text-sm"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedArticles.map((article) => (
              <Link
                key={article.id}
                to={buildArticleUrl(article.slug)}
                className="group bg-white border border-gray-200 hover:border-brand-green rounded-lg overflow-hidden smooth-transition hover-lift"
                data-testid={`related-article-${article.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={getArticleImage(article)}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 smooth-transition"
                    loading="lazy"
                    onError={handleImageError}
                  />
                </div>
                <div className="p-4">
                  <span className="text-xs font-bold text-brand-red uppercase tracking-wide">
                    {article.category_name}
                  </span>
                  <h3 className="font-playfair font-bold text-lg text-gray-900 group-hover:text-brand-green smooth-transition mt-2 line-clamp-2">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
                    <Calendar className="w-3 h-3" />
                    <span>{format(new Date(article.created_at), 'MMM dd, yyyy')}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {/* Related Deals */}
      {relatedDeals.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-playfair font-bold text-brand-green flex items-center gap-2">
              <Tag className="w-7 h-7 text-brand-red" />
              Related Deals
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedDeals.map((article) => (
              <Link
                key={article.id}
                to={buildArticleUrl(article.slug)}
                className="group bg-gradient-to-br from-brand-green to-brand-green/90 text-white rounded-lg overflow-hidden smooth-transition hover-lift"
                data-testid={`related-deal-${article.slug}`}
              >
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-brand-yellow text-brand-green text-xs font-bold px-2 py-1 rounded">
                      {article.deal_boxes[0]?.discount || 'DEAL'}
                    </span>
                  </div>
                  <h3 className="font-playfair font-bold text-lg line-clamp-2 mb-3">
                    {article.title}
                  </h3>
                  <p className="text-gray-200 text-sm line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-1 text-brand-yellow font-semibold text-sm group-hover:underline">
                    Get Deal <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default RelatedArticles;
